import { APIError, useApplicantAPI } from "@certn/api";
import { useAuth } from "@certn/auth";
import { CascadiaTheme, ThemeContext } from "@certn/cascadia";
import {
  BrandProvider,
  Container,
  Loader,
  SectionMessage,
} from "@certn/cascadia-react";
import { parseName } from "@certn/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { ApplicantDetailsPage } from "./ApplicantDetailsPage";
import { useCreateUserInAllRegions } from "./useCreateUserInAllRegions";
import { IS_LOCALHOST } from "../../constants";
import { FullScreenModalLayout } from "../components/layouts/FullScreenModalLayout";
import { Schemas } from "../types";
import { getName } from "../utils/user-utils";

export const ProfileContext = createContext<{
  profile: Schemas["PersonalInformation"] | undefined;
  refresh: () => void;
}>(undefined!);

export function ProfileProvider(props: PropsWithChildren) {
  const { t } = useTranslation();
  const { enableFpCoBrandingV2 } = useFlags();
  const [searchParams] = useSearchParams();
  const hasCleanedUpRef = useRef(false);

  const subjectIdFromUrl =
    searchParams.get("subject_id") || searchParams.get("person_id");
  const subjectIdFromStorage = localStorage.getItem("pending_subject_id");
  const subjectId = subjectIdFromUrl || subjectIdFromStorage || undefined;

  const { logout } = useAuth();
  const { setTheme } = useContext(ThemeContext);

  const {
    data,
    error,
    isLoading,
    mutate: mutatePersonalInfo,
  } = useApplicantAPI(
    "/api/internal/applicant/me/personal-info/",
    {},
    {
      shouldRetryOnError: false,
    }
  );
  // Preferences requires HasApplicantProfile — skip the fetch until we know
  // the applicant has a profile to avoid 403-spam during the setup flow.
  const hasProfile = !!data;
  const { data: preferences } = useApplicantAPI(
    hasProfile ? "/api/internal/applicant/me/preferences/" : null,
    {},

    {
      shouldRetryOnError: false,
    }
  );
  const cobrandingUrl =
    `/api/internal/applicant/case/cobranding/${subjectId ? `?subject_id=${encodeURIComponent(subjectId)}` : ""}` as "/api/internal/applicant/case/cobranding/";
  const { data: cobranding } = useApplicantAPI(
    cobrandingUrl,
    {},
    {
      shouldRetryOnError: false,
    }
  );

  useEffect(() => {
    if (!preferences) {
      return;
    }

    setTheme(preferences.theme?.toLowerCase() as CascadiaTheme);
  }, [preferences, setTheme]);

  useCreateUserInAllRegions();

  const refreshData = async () => {
    // Clean up localStorage after successful user creation
    // This ensures ApplicantDetailsPage has access to the value during form submission
    if (!hasCleanedUpRef.current && subjectIdFromStorage) {
      localStorage.removeItem("pending_subject_id");
      hasCleanedUpRef.current = true;
    }

    await mutatePersonalInfo();
  };

  const handleLogout = () => {
    // Clean up localStorage to prevent stale cobranding on next login
    localStorage.removeItem("pending_subject_id");
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  if (isLoading && !error) {
    return (
      <FullScreenModalLayout onClose={handleLogout}>
        <Loader overlay />
      </FullScreenModalLayout>
    );
  }

  if (!data) {
    if (error instanceof APIError && error.status === 404) {
      const primaryColor = enableFpCoBrandingV2
        ? cobranding?.org_primary_color ?? undefined
        : undefined;

      return (
        <FullScreenModalLayout layout="auth" onClose={handleLogout}>
          <BrandProvider primaryColor={primaryColor}>
            <ApplicantDetailsPage
              onSuccess={refreshData}
              subjectId={subjectId}
            />
          </BrandProvider>
        </FullScreenModalLayout>
      );
    }

    return (
      <FullScreenModalLayout layout="auth" onClose={handleLogout}>
        <Container>
          <SectionMessage
            type="danger"
            sectionTitle={t("System Error")}
            icon="info"
          >
            {IS_LOCALHOST ? String(error) : t("Something went wrong")}
          </SectionMessage>
        </Container>
      </FullScreenModalLayout>
    );
  }

  return (
    <ProfileContext.Provider
      value={{ profile: data, refresh: mutatePersonalInfo }}
    >
      {props.children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const { profile, refresh } = useContext(ProfileContext);

  if (!profile) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }

  return { profile, refresh };
}

export function useProfileName() {
  const { profile, refresh } = useProfile();

  return { name: parseName(getName(profile)), refresh };
}
